'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { analytics } from '@/lib/analytics';

interface ServiceCardProps {
  service: {
    id: string;
    title: string;
    description: string;
    image: string;
    alt: string;
    benefits: string[];
    frequency: string;
    icon: string;
    features: {
      duration: string;
      frequency: string;
      difficulty: string;
      season: string;
    };
    pricingTiers?: Array<{ size: string; price: string }>;
    startingPrice?: number;
    averageProject?: string;
    minimumPrice?: number;
  };
}

export default function ServiceCard({ service }: ServiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  return (
    <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-brand overflow-hidden transition-smooth hover:shadow-brand-lg hover:scale-[1.02] border-2 border-transparent hover:border-blue-200">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={service.image}
          alt={service.alt}
          className={`w-full h-full ${service.id === 'gutter-cleaning' ? 'object-cover object-center' : 'object-cover'} transition-smooth hover:scale-110 brightness-105`}
        />
        <div className="absolute top-4 right-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 rounded-full shadow-brand">
          <Icon name={service.icon as any} size={24} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
      </div>
      
      <div className="p-6">
        <div className="flex items-center mb-3">
          <Icon name={service.icon} size={24} className="text-primary mr-3" />
          <h3 className="text-xl font-semibold text-foreground">{service.title}</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{service.description}</p>
        
        <div className="flex items-center text-sm text-blue-600 mb-4 bg-blue-50 px-3 py-2 rounded-lg">
          <Icon name="ClockIcon" size={18} className="mr-2" />
          <span className="font-medium">Recommended: {service.frequency}</span>
        </div>
        
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center text-primary font-medium text-sm mb-4 transition-smooth hover:text-secondary btn-bubbly"
        >
          <span>{isExpanded ? 'Hide' : 'View'} Benefits</span>
          <Icon 
            name="ChevronDownIcon" 
            size={20} 
            className={`ml-1 transition-smooth ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
        
        {isExpanded && (
          <ul className="space-y-2 mb-4">
            {service.benefits.map((benefit, index) => (
              <li key={index} className="flex items-start text-sm text-muted-foreground">
                <Icon name="CheckCircleIcon" size={18} className="text-success mr-2 mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        )}
        
        {service.options && (
          <>
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="flex items-center text-blue-600 font-medium text-sm mb-4 transition-smooth hover:text-blue-700 btn-bubbly"
            >
              <span>{showOptions ? 'Hide' : 'View'} Service Options</span>
              <Icon 
                name="ChevronDownIcon" 
                size={20} 
                className={`ml-1 transition-smooth ${showOptions ? 'rotate-180' : ''}`}
              />
            </button>
            
            {showOptions && (
              <div className="mb-4 space-y-3 bg-blue-50 p-4 rounded-lg">
                {service.options.stories && (
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">Number of Stories</h4>
                    <div className="space-y-1">
                      {service.options.stories.map((option, idx) => (
                        <div key={idx} className="text-sm text-muted-foreground">
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {service.options.homeSize && (
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">Home Size</h4>
                    <div className="space-y-1">
                      {service.options.homeSize.map((option, idx) => (
                        <div key={idx} className="text-sm text-muted-foreground">
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {service.options.serviceType && (
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">Service Type</h4>
                    <div className="space-y-1">
                      {service.options.serviceType.map((option, idx) => (
                        <div key={idx} className="text-sm text-muted-foreground">
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {service.options.surfaces && (
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">Surface Options</h4>
                    <div className="space-y-1">
                      {service.options.surfaces.map((option, idx) => (
                        <div key={idx} className="text-sm text-muted-foreground">
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        
        <Link
          href="/get-quote"
          className="block w-full text-center px-6 py-3 bg-green-600 text-white font-semibold rounded-full hover:bg-green-700 transition-smooth shadow-brand-sm btn-bubbly"
          onClick={() => analytics.trackServiceInterest(service.title, 'Learn More Click')}
        >
          Learn More
          <Icon name="ArrowRightIcon" size={20} className="ml-2" />
        </Link>
      </div>
    </div>
  );
}